import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Trash, 
  Plus, 
  Microphone, 
  FloppyDisk, 
  Chat, 
  FileText 
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOT_TOGGLES_LIST = [
  { key: 'deposit', label: 'Deposit Chat Bot', color: '#2563eb' },
  { key: 'withdrawal', label: 'Withdrawal Chat Bot', color: '#ea580c' },
  { key: 'support', label: 'Support Chat Bot', color: '#7e22ce' },
  { key: 'inquiry', label: 'Inquiry Chat Bot', color: '#0ea5e9' },
  { key: 'group', label: 'Group Chat Bot', color: '#10b981' },
  { key: 'coin_cut', label: 'Coin Cut Auto-Message', color: '#ef4444' },
  { key: 'withdraw_cancel', label: 'Withdraw Cancel Auto-Message', color: '#ef4444' },
  { key: 'deposit_cancel', label: 'Deposit Cancel Auto-Message', color: '#ef4444' }
];

export default function BotAutoMessages({ onBack }) {
  const [toggles, setToggles] = useState({});
  const [selectedMbTab, setSelectedMbTab] = useState('ALL MB');
  const [mbTabs, setMbTabs] = useState([
    { key: 'ALL MB', label: 'ALL MB (GLOBAL)' },
    { key: 'MB1', label: 'MB1' },
    { key: 'MB2', label: 'MB2' },
    { key: 'MB3', label: 'MB3' },
    { key: 'MB4', label: 'MB4' },
    { key: 'MB5', label: 'MB5' },
    { key: 'MB6', label: 'MB6' },
  ]);

  useEffect(() => {
    async function loadMBList() {
      try {
        const res = await axios.get(`${API}/mb-config`);
        if (res.data?.mbs) {
          const dynamicTabs = [
            { key: 'ALL MB', label: 'ALL MB (GLOBAL)' },
            ...res.data.mbs.filter(m => !m.hidden).map(m => ({ key: m.id, label: m.label || m.id }))
          ];
          setMbTabs(dynamicTabs);
        }
      } catch {}
    }
    loadMBList();
  }, []);
  
  // Template lists
  const [clientTemplates, setClientTemplates] = useState([]); // Deposit -> Client
  const [groupTemplates, setGroupTemplates] = useState([]);   // Coin Cut -> Group Extras
  const [withdrawTemplates, setWithdrawTemplates] = useState([]); // Coin Cut -> Client
  const [depositCancelTemplates, setDepositCancelTemplates] = useState([]); // Deposit Cancel
  const [withdrawSuccessTemplates, setWithdrawSuccessTemplates] = useState([]); // Withdrawal Success
  const [withdrawCancelTemplates, setWithdrawCancelTemplates] = useState([]); // Withdrawal Cancel
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfigs();
  }, [selectedMbTab]);

  const defaultDeposit = `✅ आपके ID में {amount} Coins सफलतापूर्वक जमा कर दिए गए हैं। कृपया अपनी ID को Refresh करके चेक कर लें।\nUTR: {utr}\nReceived From: {receivedFrom}\nReceived In: {receivedIn}\n✅ {amount} Coins have been successfully deposited into your ID. Please refresh your ID and check.`;
  const defaultWithdraw = `⚠️ Aapke {amount} coin cut kar liye gaye hain.\n25-30 minute ke andar aapka withdraw ho jayega.\n\nA/c Holder: {account_holder_name}\nBank A/c: {bank_account_number}\nIFSC: {ifsc}\nBank Name: {bank_name}\nUPI: {upi_id}\n\n🕒 Agle 10 minute mein aap bank detail change karva sakte hain.\n10 minute baad hamari zimmedari nahi hogi — isi account mein withdraw ho jayega.`;
  const defaultGroup = `Bank details snapshot with Reference: {reference}`;
  
  const defaultDepositCancel = `किसी कारणवश आपका last deposit ₹{amount} cancel हो गया है। इसके बारे में आप हमारे support person से बात कर सकते हैं।\nReason: {reason}`;
  const defaultWithdrawSuccess = `✅ Aapka withdraw ho gaya hai.
UTR Number: {utr}
Paid From: {paid_from}

Iss bank account mein paisa gaya hai:
A/c Holder: {account_holder_name}
Bank A/c: {bank_account_number}
IFSC: {ifsc}
Bank Name: {bank_name}
UPI: {upi_id}

Amount: {amount}
Jaakar apna bank check kar lein.`;
  const defaultWithdrawCancel = `किसी कारणवश आपका last withdrawal ₹{amount} cancel हो गया है। इसके बारे में आप हमारे support person से बात कर सकते हैं। Reason: {reason}`;

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/mb-config/bot-settings?segment=${selectedMbTab}`);
      setToggles(response.data.toggles || {});
      
      const rawTemplate = response.data.template || '';
      try {
        if (rawTemplate.trim().startsWith('{')) {
          const parsed = JSON.parse(rawTemplate);
          
          let ct = parsed.client_templates || [];
          let gt = parsed.group_templates || [];
          let wt = parsed.withdraw_templates || [];
          let dct = parsed.deposit_cancel_templates || [];
          let wst = parsed.withdraw_success_templates || [];
          let wct = parsed.withdraw_cancel_templates || [];
          
          if (ct.length === 0) {
            ct = [{ id: 'default-dep', text: defaultDeposit, link: '', lang: 'English', format: 'Plain' }];
          }
          if (gt.length === 0) {
            gt = [{ id: 'default-grp', text: defaultGroup, link: '', lang: 'English', format: 'Plain' }];
          }
          if (wt.length === 0) {
            wt = [{ id: 'default-wth', text: defaultWithdraw, link: '', lang: 'English', format: 'Plain' }];
          }
          if (dct.length === 0) {
            dct = [{ id: 'default-dep-cxl', text: defaultDepositCancel, link: '', lang: 'English', format: 'Plain' }];
          }
          if (wst.length === 0) {
            wst = [{ id: 'default-wth-suc', text: defaultWithdrawSuccess, link: '', lang: 'English', format: 'Plain' }];
          }
          if (wct.length === 0) {
            wct = [{ id: 'default-wth-cxl', text: defaultWithdrawCancel, link: '', lang: 'English', format: 'Plain' }];
          }

          setClientTemplates(ct);
          setGroupTemplates(gt);
          setWithdrawTemplates(wt);
          setDepositCancelTemplates(dct);
          setWithdrawSuccessTemplates(wst);
          setWithdrawCancelTemplates(wct);
        } else {
          // Fallback if it's legacy plain text
          setClientTemplates([{ id: Date.now(), text: defaultDeposit, link: '', lang: 'English', format: 'Plain' }]);
          setGroupTemplates([{ id: Date.now() + 1, text: defaultGroup, link: '', lang: 'English', format: 'Plain' }]);
          setWithdrawTemplates([{ id: Date.now() + 2, text: defaultWithdraw, link: '', lang: 'English', format: 'Plain' }]);
          setDepositCancelTemplates([{ id: Date.now() + 3, text: defaultDepositCancel, link: '', lang: 'English', format: 'Plain' }]);
          setWithdrawSuccessTemplates([{ id: Date.now() + 4, text: defaultWithdrawSuccess, link: '', lang: 'English', format: 'Plain' }]);
          setWithdrawCancelTemplates([{ id: Date.now() + 5, text: defaultWithdrawCancel, link: '', lang: 'English', format: 'Plain' }]);
        }
      } catch {
        setClientTemplates([{ id: Date.now(), text: defaultDeposit, link: '', lang: 'English', format: 'Plain' }]);
        setGroupTemplates([{ id: Date.now() + 1, text: defaultGroup, link: '', lang: 'English', format: 'Plain' }]);
        setWithdrawTemplates([{ id: Date.now() + 2, text: defaultWithdraw, link: '', lang: 'English', format: 'Plain' }]);
        setDepositCancelTemplates([{ id: Date.now() + 3, text: defaultDepositCancel, link: '', lang: 'English', format: 'Plain' }]);
        setWithdrawSuccessTemplates([{ id: Date.now() + 4, text: defaultWithdrawSuccess, link: '', lang: 'English', format: 'Plain' }]);
        setWithdrawCancelTemplates([{ id: Date.now() + 5, text: defaultWithdrawCancel, link: '', lang: 'English', format: 'Plain' }]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    const updated = { ...toggles, [key]: !toggles[key] };
    setToggles(updated);
    try {
      await axios.post(`${API}/mb-config/bot-settings/toggle`, {
        segment: selectedMbTab,
        key,
        value: updated[key]
      });
      toast.success('Toggle updated');
    } catch (err) {
      toast.error('Failed to update toggle');
    }
  };

  const handleSaveAll = async () => {
    try {
      const payload = {
        client_templates: clientTemplates,
        group_templates: groupTemplates,
        withdraw_templates: withdrawTemplates,
        deposit_cancel_templates: depositCancelTemplates,
        withdraw_success_templates: withdrawSuccessTemplates,
        withdraw_cancel_templates: withdrawCancelTemplates
      };
      
      await axios.post(`${API}/mb-config/bot-settings/template`, {
        segment: selectedMbTab,
        template: JSON.stringify(payload)
      });
      toast.success('Templates saved successfully!');
    } catch (err) {
      toast.error('Failed to save templates');
    }
  };

  const addTemplate = (type) => {
    const defaultTextMap = {
      client: defaultDeposit,
      group: defaultGroup,
      withdraw: defaultWithdraw,
      depositCancel: defaultDepositCancel,
      withdrawSuccess: defaultWithdrawSuccess,
      withdrawCancel: defaultWithdrawCancel
    };
    const newItem = {
      id: Date.now() + Math.random(),
      text: defaultTextMap[type] || '',
      link: '',
      lang: 'English',
      format: 'Plain'
    };
    if (type === 'client') {
      setClientTemplates([...clientTemplates, newItem]);
    } else if (type === 'group') {
      setGroupTemplates([...groupTemplates, newItem]);
    } else if (type === 'withdraw') {
      setWithdrawTemplates([...withdrawTemplates, newItem]);
    } else if (type === 'depositCancel') {
      setDepositCancelTemplates([...depositCancelTemplates, newItem]);
    } else if (type === 'withdrawSuccess') {
      setWithdrawSuccessTemplates([...withdrawSuccessTemplates, newItem]);
    } else if (type === 'withdrawCancel') {
      setWithdrawCancelTemplates([...withdrawCancelTemplates, newItem]);
    }
  };

  const removeTemplate = (type, id) => {
    if (type === 'client') {
      setClientTemplates(clientTemplates.filter(item => item.id !== id));
    } else if (type === 'group') {
      setGroupTemplates(groupTemplates.filter(item => item.id !== id));
    } else if (type === 'withdraw') {
      setWithdrawTemplates(withdrawTemplates.filter(item => item.id !== id));
    } else if (type === 'depositCancel') {
      setDepositCancelTemplates(depositCancelTemplates.filter(item => item.id !== id));
    } else if (type === 'withdrawSuccess') {
      setWithdrawSuccessTemplates(withdrawSuccessTemplates.filter(item => item.id !== id));
    } else if (type === 'withdrawCancel') {
      setWithdrawCancelTemplates(withdrawCancelTemplates.filter(item => item.id !== id));
    }
  };

  const updateTemplateField = (type, id, field, value) => {
    if (type === 'client') {
      setClientTemplates(clientTemplates.map(item => item.id === id ? { ...item, [field]: value } : item));
    } else if (type === 'group') {
      setGroupTemplates(groupTemplates.map(item => item.id === id ? { ...item, [field]: value } : item));
    } else if (type === 'withdraw') {
      setWithdrawTemplates(withdrawTemplates.map(item => item.id === id ? { ...item, [field]: value } : item));
    } else if (type === 'depositCancel') {
      setDepositCancelTemplates(depositCancelTemplates.map(item => item.id === id ? { ...item, [field]: value } : item));
    } else if (type === 'withdrawSuccess') {
      setWithdrawSuccessTemplates(withdrawSuccessTemplates.map(item => item.id === id ? { ...item, [field]: value } : item));
    } else if (type === 'withdrawCancel') {
      setWithdrawCancelTemplates(withdrawCancelTemplates.map(item => item.id === id ? { ...item, [field]: value } : item));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      

      {/* Main body scroll */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 max-w-7xl mx-auto w-full">
        
        {/* Toggles Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Bot Active / Inactive</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BOT_TOGGLES_LIST.map((bot) => {
              const isChecked = !!toggles[bot.key];
              return (
                <div 
                  key={bot.key} 
                  onClick={() => handleToggle(bot.key)}
                  className="flex items-center justify-between p-4 border border-gray-150 rounded-xl hover:bg-gray-50/50 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-2.5 h-2.5 rounded-full" 
                      style={{ backgroundColor: bot.color }}
                    />
                    <span className="font-bold text-sm text-gray-700">{bot.label}</span>
                  </div>
                  
                  {/* Custom checkmark square */}
                  <div className={`w-6 h-6 border-2 rounded-md flex items-center justify-center transition-all ${
                    isChecked 
                      ? 'border-[#008069] bg-[#008069] text-white' 
                      : 'border-gray-300 bg-white'
                  }`}>
                    {isChecked && (
                      <span className="text-xs font-black">✓</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Templates Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between flex-wrap gap-4 border-b pb-4 mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">Auto-Message Templates</h2>
              <p className="text-xs text-gray-400 mt-1">
                Placeholders: <span className="font-mono text-gray-500 font-bold bg-gray-50 px-1 py-0.5 rounded">{`{amount}`}</span>, <span className="font-mono text-gray-500 font-bold bg-gray-50 px-1 py-0.5 rounded">{`{username}`}</span>, <span className="font-mono text-gray-500 font-bold bg-gray-50 px-1 py-0.5 rounded">{`{utr}`}</span>, <span className="font-mono text-gray-500 font-bold bg-gray-50 px-1 py-0.5 rounded">{`{bank}`}</span>, <span className="font-mono text-gray-500 font-bold bg-gray-50 px-1 py-0.5 rounded">{`{reference}`}</span>, <span className="font-mono text-gray-500 font-bold bg-gray-50 px-1 py-0.5 rounded">{`{reason}`}</span>
              </p>
            </div>
            
            <button
              onClick={handleSaveAll}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#008069] text-white hover:bg-[#006e5a] rounded-xl font-bold transition-all cursor-pointer shadow-sm text-sm"
            >
              <FloppyDisk size={18} weight="bold" />
              Save
            </button>
          </div>

          {/* MB Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-4 border-b border-gray-100 mb-6">
            {mbTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSelectedMbTab(tab.key)}
                className={`px-4 py-2 rounded-full font-bold whitespace-nowrap text-xs transition-colors cursor-pointer ${
                  selectedMbTab === tab.key
                    ? 'bg-[#4f46e5] text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-650'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-10 text-gray-450">Loading templates...</div>
          ) : (
            <div className="space-y-8">
              
              {/* Coin Cut -> Client Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-bold text-gray-800 text-sm">Coin Cut → Client</h3>
                  <button 
                    onClick={() => addTemplate('withdraw')}
                    className="flex items-center gap-1 text-[#008069] hover:text-[#006e5a] text-xs font-bold bg-green-50 px-2.5 py-1.5 rounded-lg border border-green-150 cursor-pointer"
                  >
                    <Plus size={14} weight="bold" /> Add
                  </button>
                </div>

                {withdrawTemplates.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">No withdraw templates added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {withdrawTemplates.map((item, idx) => (
                      <div key={item.id} className="bg-gray-50/50 p-4 rounded-xl border border-gray-150 space-y-3">
                        <div className="flex gap-3">
                          <textarea
                            value={item.text}
                            onChange={(e) => updateTemplateField('withdraw', item.id, 'text', e.target.value)}
                            placeholder="⚠️ Aapke ID se Coins ₹{amount} काट लिए गए है..."
                            className="flex-1 min-h-[70px] p-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm text-gray-700"
                          />
                          <div className="flex flex-col gap-2">
                            <button 
                              type="button"
                              onClick={() => {
                                updateTemplateField('withdraw', item.id, 'text', item.text + ' [Voice template transcribed]');
                                toast.info('Voice input simulation triggered');
                              }}
                              className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl cursor-pointer"
                            >
                              <Microphone size={18} weight="bold" />
                            </button>
                            <button 
                              type="button"
                              className="p-2.5 bg-[#008069] hover:bg-[#006e5a] text-white rounded-xl cursor-pointer"
                            >
                              <FileText size={18} weight="bold" />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <input
                            type="text"
                            value={item.link || ''}
                            onChange={(e) => updateTemplateField('withdraw', item.id, 'link', e.target.value)}
                            placeholder="Optional link (https://...)"
                            className="flex-1 min-w-[200px] px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none"
                          />
                          <div className="flex items-center gap-2">
                            <select
                              value={item.lang || 'English'}
                              onChange={(e) => updateTemplateField('withdraw', item.id, 'lang', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="English">English</option>
                              <option value="Hindi">Hindi</option>
                            </select>

                            <select
                              value={item.format || 'Plain'}
                              onChange={(e) => updateTemplateField('withdraw', item.id, 'format', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="Plain">Plain</option>
                              <option value="Bold">Bold</option>
                            </select>

                            <button
                              type="button"
                              onClick={() => removeTemplate('withdraw', item.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              <Trash size={16} weight="bold" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Coin Cut -> Group Extras Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-bold text-gray-800 text-sm">Coin Cut → Group Extras</h3>
                  <button 
                    onClick={() => addTemplate('group')}
                    className="flex items-center gap-1 text-[#008069] hover:text-[#006e5a] text-xs font-bold bg-green-50 px-2.5 py-1.5 rounded-lg border border-green-150 cursor-pointer"
                  >
                    <Plus size={14} weight="bold" /> Add
                  </button>
                </div>

                {groupTemplates.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">No group extra templates added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {groupTemplates.map((item, idx) => (
                      <div key={item.id} className="bg-gray-50/50 p-4 rounded-xl border border-gray-150 space-y-3">
                        <div className="flex gap-3">
                          <textarea
                            value={item.text}
                            onChange={(e) => updateTemplateField('group', item.id, 'text', e.target.value)}
                            placeholder="Bank details snapshot with Reference: {reference}"
                            className="flex-1 min-h-[70px] p-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm text-gray-700"
                          />
                          <div className="flex flex-col gap-2">
                            <button 
                              type="button"
                              onClick={() => {
                                updateTemplateField('group', item.id, 'text', item.text + ' [Voice template transcribed]');
                                toast.info('Voice input simulation triggered');
                              }}
                              className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl cursor-pointer"
                            >
                              <Microphone size={18} weight="bold" />
                            </button>
                            <button 
                              type="button"
                              className="p-2.5 bg-[#008069] hover:bg-[#006e5a] text-white rounded-xl cursor-pointer"
                            >
                              <FileText size={18} weight="bold" />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <input
                            type="text"
                            value={item.link || ''}
                            onChange={(e) => updateTemplateField('group', item.id, 'link', e.target.value)}
                            placeholder="Optional link (https://...)"
                            className="flex-1 min-w-[200px] px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none"
                          />
                          <div className="flex items-center gap-2">
                            <select
                              value={item.lang || 'English'}
                              onChange={(e) => updateTemplateField('group', item.id, 'lang', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="English">English</option>
                              <option value="Hindi">Hindi</option>
                            </select>

                            <select
                              value={item.format || 'Plain'}
                              onChange={(e) => updateTemplateField('group', item.id, 'format', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="Plain">Plain</option>
                              <option value="Bold">Bold</option>
                            </select>

                            <button
                              type="button"
                              onClick={() => removeTemplate('group', item.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              <Trash size={16} weight="bold" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Deposit -> Client Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-bold text-gray-800 text-sm">Deposit → Client</h3>
                  <button 
                    onClick={() => addTemplate('client')}
                    className="flex items-center gap-1 text-[#008069] hover:text-[#006e5a] text-xs font-bold bg-green-50 px-2.5 py-1.5 rounded-lg border border-green-150 cursor-pointer"
                  >
                    <Plus size={14} weight="bold" /> Add
                  </button>
                </div>

                {clientTemplates.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">No deposit templates added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {clientTemplates.map((item, idx) => (
                      <div key={item.id} className="bg-gray-50/50 p-4 rounded-xl border border-gray-150 space-y-3">
                        <div className="flex gap-3">
                          <textarea
                            value={item.text}
                            onChange={(e) => updateTemplateField('client', item.id, 'text', e.target.value)}
                            placeholder="✅ आपके ID में Coins सफलतापूर्वक जमा कर दिए गए हैं..."
                            className="flex-1 min-h-[70px] p-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm text-gray-700"
                          />
                          <div className="flex flex-col gap-2">
                            <button 
                              type="button"
                              onClick={() => {
                                updateTemplateField('client', item.id, 'text', item.text + ' [Voice template transcribed]');
                                toast.info('Voice input simulation triggered');
                              }}
                              className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl cursor-pointer"
                            >
                              <Microphone size={18} weight="bold" />
                            </button>
                            <button 
                              type="button"
                              className="p-2.5 bg-[#008069] hover:bg-[#006e5a] text-white rounded-xl cursor-pointer"
                            >
                              <FileText size={18} weight="bold" />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <input
                            type="text"
                            value={item.link || ''}
                            onChange={(e) => updateTemplateField('client', item.id, 'link', e.target.value)}
                            placeholder="Optional link (https://...)"
                            className="flex-1 min-w-[200px] px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none"
                          />
                          <div className="flex items-center gap-2">
                            <select
                              value={item.lang || 'English'}
                              onChange={(e) => updateTemplateField('client', item.id, 'lang', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="English">English</option>
                              <option value="Hindi">Hindi</option>
                            </select>

                            <select
                              value={item.format || 'Plain'}
                              onChange={(e) => updateTemplateField('client', item.id, 'format', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="Plain">Plain</option>
                              <option value="Bold">Bold</option>
                            </select>

                            <button
                              type="button"
                              onClick={() => removeTemplate('client', item.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              <Trash size={16} weight="bold" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Deposit Cancel Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-bold text-gray-800 text-sm">Deposit Cancel</h3>
                  <button 
                    onClick={() => addTemplate('depositCancel')}
                    className="flex items-center gap-1 text-[#008069] hover:text-[#006e5a] text-xs font-bold bg-green-50 px-2.5 py-1.5 rounded-lg border border-green-150 cursor-pointer"
                  >
                    <Plus size={14} weight="bold" /> Add
                  </button>
                </div>

                {depositCancelTemplates.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">No deposit cancel templates added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {depositCancelTemplates.map((item, idx) => (
                      <div key={item.id} className="bg-gray-50/50 p-4 rounded-xl border border-gray-150 space-y-3">
                        <div className="flex gap-3">
                          <textarea
                            value={item.text}
                            onChange={(e) => updateTemplateField('depositCancel', item.id, 'text', e.target.value)}
                            placeholder="किसी कारणवश आपका last deposit ₹{amount} cancel हो गया है..."
                            className="flex-1 min-h-[70px] p-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm text-gray-700"
                          />
                          <div className="flex flex-col gap-2">
                            <button 
                              type="button"
                              onClick={() => {
                                updateTemplateField('depositCancel', item.id, 'text', item.text + ' [Voice template transcribed]');
                                toast.info('Voice input simulation triggered');
                              }}
                              className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl cursor-pointer"
                            >
                              <Microphone size={18} weight="bold" />
                            </button>
                            <button 
                              type="button"
                              className="p-2.5 bg-[#008069] hover:bg-[#006e5a] text-white rounded-xl cursor-pointer"
                            >
                              <FileText size={18} weight="bold" />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <input
                            type="text"
                            value={item.link || ''}
                            onChange={(e) => updateTemplateField('depositCancel', item.id, 'link', e.target.value)}
                            placeholder="Optional link (https://...)"
                            className="flex-1 min-w-[200px] px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none"
                          />
                          <div className="flex items-center gap-2">
                            <select
                              value={item.lang || 'English'}
                              onChange={(e) => updateTemplateField('depositCancel', item.id, 'lang', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="English">English</option>
                              <option value="Hindi">Hindi</option>
                            </select>

                            <select
                              value={item.format || 'Plain'}
                              onChange={(e) => updateTemplateField('depositCancel', item.id, 'format', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="Plain">Plain</option>
                              <option value="Bold">Bold</option>
                            </select>

                            <button
                              type="button"
                              onClick={() => removeTemplate('depositCancel', item.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              <Trash size={16} weight="bold" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Withdrawal Success Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-bold text-gray-800 text-sm">Withdrawal Success</h3>
                  <button 
                    onClick={() => addTemplate('withdrawSuccess')}
                    className="flex items-center gap-1 text-[#008069] hover:text-[#006e5a] text-xs font-bold bg-green-50 px-2.5 py-1.5 rounded-lg border border-green-150 cursor-pointer"
                  >
                    <Plus size={14} weight="bold" /> Add
                  </button>
                </div>

                {withdrawSuccessTemplates.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">No withdrawal success templates added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {withdrawSuccessTemplates.map((item, idx) => (
                      <div key={item.id} className="bg-gray-50/50 p-4 rounded-xl border border-gray-150 space-y-3">
                        <div className="flex gap-3">
                          <textarea
                            value={item.text}
                            onChange={(e) => updateTemplateField('withdrawSuccess', item.id, 'text', e.target.value)}
                            placeholder="✅ Aapka withdraw ho gaya hai..."
                            className="flex-1 min-h-[70px] p-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm text-gray-700"
                          />
                          <div className="flex flex-col gap-2">
                            <button 
                              type="button"
                              onClick={() => {
                                updateTemplateField('withdrawSuccess', item.id, 'text', item.text + ' [Voice template transcribed]');
                                toast.info('Voice input simulation triggered');
                              }}
                              className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl cursor-pointer"
                            >
                              <Microphone size={18} weight="bold" />
                            </button>
                            <button 
                              type="button"
                              className="p-2.5 bg-[#008069] hover:bg-[#006e5a] text-white rounded-xl cursor-pointer"
                            >
                              <FileText size={18} weight="bold" />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <input
                            type="text"
                            value={item.link || ''}
                            onChange={(e) => updateTemplateField('withdrawSuccess', item.id, 'link', e.target.value)}
                            placeholder="Optional link (https://...)"
                            className="flex-1 min-w-[200px] px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none"
                          />
                          <div className="flex items-center gap-2">
                            <select
                              value={item.lang || 'English'}
                              onChange={(e) => updateTemplateField('withdrawSuccess', item.id, 'lang', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="English">English</option>
                              <option value="Hindi">Hindi</option>
                            </select>

                            <select
                              value={item.format || 'Plain'}
                              onChange={(e) => updateTemplateField('withdrawSuccess', item.id, 'format', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="Plain">Plain</option>
                              <option value="Bold">Bold</option>
                            </select>

                            <button
                              type="button"
                              onClick={() => removeTemplate('withdrawSuccess', item.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              <Trash size={16} weight="bold" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Withdrawal Cancel Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-bold text-gray-800 text-sm">Withdrawal Cancel</h3>
                  <button 
                    onClick={() => addTemplate('withdrawCancel')}
                    className="flex items-center gap-1 text-[#008069] hover:text-[#006e5a] text-xs font-bold bg-green-50 px-2.5 py-1.5 rounded-lg border border-green-150 cursor-pointer"
                  >
                    <Plus size={14} weight="bold" /> Add
                  </button>
                </div>

                {withdrawCancelTemplates.length === 0 ? (
                  <p className="text-xs text-gray-400 italic py-2">No withdrawal cancel templates added yet.</p>
                ) : (
                  <div className="space-y-4">
                    {withdrawCancelTemplates.map((item, idx) => (
                      <div key={item.id} className="bg-gray-50/50 p-4 rounded-xl border border-gray-150 space-y-3">
                        <div className="flex gap-3">
                          <textarea
                            value={item.text}
                            onChange={(e) => updateTemplateField('withdrawCancel', item.id, 'text', e.target.value)}
                            placeholder="किसी कारणवश आपका last withdrawal ₹{amount} cancel हो गया है..."
                            className="flex-1 min-h-[70px] p-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm text-gray-700"
                          />
                          <div className="flex flex-col gap-2">
                            <button 
                              type="button"
                              onClick={() => {
                                updateTemplateField('withdrawCancel', item.id, 'text', item.text + ' [Voice template transcribed]');
                                toast.info('Voice input simulation triggered');
                              }}
                              className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl cursor-pointer"
                            >
                              <Microphone size={18} weight="bold" />
                            </button>
                            <button 
                              type="button"
                              className="p-2.5 bg-[#008069] hover:bg-[#006e5a] text-white rounded-xl cursor-pointer"
                            >
                              <FileText size={18} weight="bold" />
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <input
                            type="text"
                            value={item.link || ''}
                            onChange={(e) => updateTemplateField('withdrawCancel', item.id, 'link', e.target.value)}
                            placeholder="Optional link (https://...)"
                            className="flex-1 min-w-[200px] px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:outline-none"
                          />
                          <div className="flex items-center gap-2">
                            <select
                              value={item.lang || 'English'}
                              onChange={(e) => updateTemplateField('withdrawCancel', item.id, 'lang', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="English">English</option>
                              <option value="Hindi">Hindi</option>
                            </select>

                            <select
                              value={item.format || 'Plain'}
                              onChange={(e) => updateTemplateField('withdrawCancel', item.id, 'format', e.target.value)}
                              className="px-2 py-1.5 bg-white border border-gray-250 rounded-lg text-xs"
                            >
                              <option value="Plain">Plain</option>
                              <option value="Bold">Bold</option>
                            </select>

                            <button
                              type="button"
                              onClick={() => removeTemplate('withdrawCancel', item.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              <Trash size={16} weight="bold" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>

      </div>
    </div>
  );
}
